import * as React from 'react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { Skeleton, Tag, message } from 'antd'
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { inject, observer } from 'mobx-react'
import { DiscountModels } from '@getgreenline/products'
import { PaginationState, TApplyDiscount } from './Discount.interface'
import { isEmpty } from 'lodash'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props {
  loading: boolean
  state: PaginationState
  data: DiscountModels.IDiscount[]
  onClickToEditDiscount: (discountId: number) => void
}

function discountAppliedToProductType(discount: DiscountModels.IDiscount): TApplyDiscount {
  switch (true) {
    case discount.products.length === 0 && discount.categories.length === 0:
      return 'all'
    case discount.products.length === 0 &&
      discount.categories.every((category) => category.type === 'exclude'):
      return 'all'

    case discount.categories.length === 0 &&
      discount.products.every((product) => product.type === 'exclude'):
      return 'all'

    case discount.products.every((product) => product.type === 'exclude') &&
      discount.categories.every((category) => category.type === 'exclude'):
      return 'all'

    case discount.products.some((product) => product.type === 'include') &&
      discount.categories.some((category) => category.type === 'include'):
      return undefined

    case discount.categories.some((category) => category.type === 'include'):
      return 'category'

    case discount.products.length > 0 && discount.categories.length === 0:
      return 'product'

    default:
      return undefined
  }
}

function getApplyTypeString({
  applyType,
  discountAppliedToProductType,
}: {
  applyType: DiscountModels.ApplyType
  discountAppliedToProductType: TApplyDiscount
}) {
  if (applyType === 'products') return 'products'
  if (applyType === 'cart') {
    if (discountAppliedToProductType === 'product') return 'bundle'
    return 'entire cart'
  }
  return '-'
}

const _DiscountsTable: React.FC<InjectedProps & Props> = ({
  loading,
  state,
  data,
  onClickToEditDiscount,
  currentCompanyStore,
}) => {
  const canUseSalesChannel = currentCompanyStore?.canUseSalesChannel
  const canUseDiscountMultipleLoyaltyTiers = currentCompanyStore?.canUseDiscountMultipleLoyaltyTiers

  const canUseExternalLoyalty = !!currentCompanyStore?.canUseExternalLoyalty
  const canAccessLoyalty = !!currentCompanyStore?.company?.permissions.canAccessLoyalty

  const discountLocations = (locationIds: number[]) => {
    if (locationIds.length === 0) {
      return <div>All</div>
    } else {
      return (
        <ul style={{ paddingLeft: '6%', marginBottom: 0 }}>
          {locationIds.map((id) => {
            const location = currentCompanyStore!.locations
              ? currentCompanyStore!.locations.find((location) => location.id === id)
              : ''
            return <li key={id}>{location ? location.name : <i>Unauthorized location</i>}</li>
          })}
        </ul>
      )
    }
  }

  const discountLoyaltyTiers = (discount: DiscountModels.IDiscount) => {
    return (
      <ul style={{ paddingLeft: '6%', marginBottom: 0 }}>
        {canUseDiscountMultipleLoyaltyTiers && !isEmpty(discount.loyaltyTiers)
          ? discount.loyaltyTiers!.map((loyaltyTier) => (
              <li key={loyaltyTier.id}>{loyaltyTier.name}</li>
            ))
          : discount.loyaltyTierName && <li>{discount.loyaltyTierName}</li>}
      </ul>
    )
  }

  const deleteDiscount = async (discount: DiscountModels.IDiscount) => {
    if (window.confirm('Are you sure you want to delete this discount?')) {
      try {
        await currentCompanyStore!.productStore!.deleteDiscount({
          discountId: discount.id,
          searchFilter: {
            offset: state.offset,
            limit: state.limit,
          },
          permissions: {
            canUseExternalLoyalty,
            canAccessLoyalty,
          },
        })

        message.success('Discount is deleted.')
      } catch (error) {
        message.error(parseErrorMsg(error) || 'An error occurred while deleting the discount')
      }
    }
  }

  return (
    <>
      {loading ? (
        <Skeleton active />
      ) : (
        <div className='intercom-pb-2' style={{ overflow: 'scroll' }}>
          <table className='table'>
            <thead className='thead-light'>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Locations</th>
                <th>Applied to</th>
                {canAccessLoyalty && <th>Loyalty tier</th>}
                <th>Amount</th>
                {canUseSalesChannel && <th>Sales Channel</th>}
                <th>Day/Time restriction</th>
                <th>Options</th>
              </tr>
            </thead>
            <tbody>
              {data.map((discount) => {
                return (
                  <tr
                    className='category-row cursor-pointer'
                    key={discount.id}
                    onClick={() => onClickToEditDiscount(discount.id)}
                  >
                    <td>
                      <span>{discount.id}</span>
                    </td>
                    <td>
                      <span>{discount.name}</span>
                    </td>
                    <td style={{ maxWidth: 250 }}>
                      <span>{discount.description || '-'}</span>
                    </td>
                    <td>{discountLocations(discount.locationIds)}</td>
                    <td className='discount-apply-type-td'>
                      <div>
                        {getApplyTypeString({
                          applyType: discount.applyType,
                          discountAppliedToProductType: discountAppliedToProductType(discount),
                        })}
                      </div>
                      {discount.products.filter((p) => p.type === 'include').length > 0 && (
                        <div className='text-muted'>
                          {discount.products.filter((p) => p.type === 'include').length} products
                          included
                        </div>
                      )}
                      {discount.products.filter((p) => p.type === 'exclude').length > 0 && (
                        <div className='text-muted'>
                          {discount.products.filter((p) => p.type === 'exclude').length} products
                          excluded
                        </div>
                      )}
                      {discount.categories.filter((p) => p.type === 'include').length > 0 && (
                        <div className='text-muted'>
                          {discount.categories.filter((p) => p.type === 'include').length}{' '}
                          categories included
                        </div>
                      )}
                      {discount.categories.filter((p) => p.type === 'exclude').length > 0 && (
                        <div className='text-muted'>
                          {discount.categories.filter((p) => p.type === 'exclude').length}{' '}
                          categories excluded
                        </div>
                      )}
                      {discount.automatic && (
                        <Tag color='blue' className='mt-2'>
                          Auto
                        </Tag>
                      )}
                    </td>
                    {canAccessLoyalty && <td>{discountLoyaltyTiers(discount)}</td>}
                    <td>
                      <span className='ml-3'>
                        {discount.type === DiscountModels.DiscountTypes.FIXED
                          ? `$${discount.amount / 100}`
                          : `${discount.amount}%`}
                      </span>
                    </td>
                    {canUseSalesChannel && (
                      <td>
                        {discount.salesChannels?.map((salesChannel) => (
                          <div key={salesChannel.id}>
                            {currentCompanyStore?.getSaleChannelDisplayName(salesChannel)}
                          </div>
                        ))}
                      </td>
                    )}
                    <td>
                      {discount.applyDateRestriction ? (
                        <span className='text-muted'>Enabled</span>
                      ) : (
                        <span className='text-muted'>-</span>
                      )}
                    </td>
                    <td>
                      <UncontrolledDropdown
                        size='sm'
                        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                      >
                        <DropdownToggle color='light'>
                          <i className='fa fas fa-ellipsis-v' />
                        </DropdownToggle>
                        <DropdownMenu className='background-shadow'>
                          <DropdownItem onClick={() => onClickToEditDiscount(discount.id)}>
                            Edit
                          </DropdownItem>
                          <DropdownItem onClick={() => deleteDiscount(discount)}>
                            <span className='text-danger'>Delete</span>
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

export const DiscountsTable = inject('currentCompanyStore')(observer(_DiscountsTable))
